import styles from "../ActivatingPage.module.css";
import logo from "../../../assets/cosmo-logo-white.svg";
import {
  Box,
  Button,
  FormControl,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Title from "../../../components/Title";
import BodyText from "../../../components/BodyText";
import ButtonContinueIcon from "../../../components/icons/ButtonContinueIcon";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { useEffect, useMemo, useState } from "react";
import API from "../../../api/API";

interface ActivatingEmailInputProps {
  confirmationNumber: string | null;
  setIsEmailValidated: (value: boolean) => void;
  setErrorMessage: (value: string) => void;
}

const ActivatingEmailInput = ({
  confirmationNumber,
  setIsEmailValidated,
  setErrorMessage,
}: ActivatingEmailInputProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [emailInputValue, setEmailInputValue] = useState("");
  const [emailInputError, setEmailInputError] = useState<string>();

  // Simple email validator
  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const disableSubmit = useMemo(() => {
    return emailInputValue === "" || !isValidEmail(emailInputValue);
  }, [emailInputValue]);

  // To get rid of linter errors
  useEffect(() => {
    setEmailInputError(undefined);
    setLoading(false);
  }, []);

  const emailInputChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEmailInputError(undefined);
    setEmailInputValue(e.target.value);
  };

  const verifyEmail = async () => {
    if (!confirmationNumber) {
      setEmailInputError("Failed to retrieve confirmation number.");
      return;
    }

    setLoading(true);
    try {
      const res = await API.post("/devices/activation/verifyActivatingEmail", {
        confirmation_number: confirmationNumber,
        email: emailInputValue,
      });
      try {
        switch (res.data.status) {
          case 1:
            setIsEmailValidated(true);
            break;

          default:
            setIsEmailValidated(false);
            setErrorMessage("Unkown Error");
            break;
        }
      } catch (e) {
        // If anything goes wrong in the response handling, display unknown error
        setIsEmailValidated(false);
        setErrorMessage("Unkown Error");
      }
    } catch (error: any) {
      setIsEmailValidated(false);

      if (!error.response) {
        setErrorMessage(
          "We failed to retrieve information for your device. Please try again after some time."
        );
        return;
      }

      if (
        (error.response.status === 400 &&
          error.response.data.message.includes("email")) ||
        error.response.data.message.includes("Email")
      ) {
        setEmailInputError(error.response.data.message);
        return;
      }

      setErrorMessage(error?.response?.data?.message ?? "Unkown Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <img src={logo} className={styles.logo} alt="cosmo logo" />
      <Paper
        sx={{
          width: "80%",
          maxWidth: 500,
          borderRadius: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, sm: 4, md: 5 }, // responsive padding
          mx: "auto",
          my: 6,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1), 0px 8px 24px rgba(0, 0, 0, 0.15)",
        }}
        elevation={3}
      >
        <Title
          sx={{
            fontWeight: 600,
            fontSize: { xs: "20px", sm: "22px", md: "24px" },
            mb: 1,
            textAlign: "center",
          }}
        >
          Verify Your Email
        </Title>
        <BodyText
          sx={{
            fontWeight: 400,
            fontSize: { xs: "13px", sm: "14px" },
            textAlign: "center",
            mb: 3,
          }}
        >
          Almost there! Confirm your email to ignite your activation.
        </BodyText>

        <Box sx={{ width: "100%" }}>
          <FormControl
            fullWidth
            sx={{
              mb: 2,
            }}
          >
            <TextField
              type="email"
              value={emailInputValue}
              onChange={emailInputChangeHandler}
              error={!!emailInputError}
              fullWidth
              placeholder="Enter Email"
            />
            {emailInputError ? (
              <Typography color="error">{emailInputError}</Typography>
            ) : undefined}
          </FormControl>

          <FormControl>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ButtonContinueIcon />}
              sx={{
                py: 1.2,
                px: 3,
                fontWeight: 600,
                fontSize: "15px",
                textTransform: "none",
              }}
              onClick={verifyEmail}
              disabled={disableSubmit}
            >
              Verify & Continue
            </Button>
          </FormControl>
        </Box>

        <LoadingOverlay loading={loading} />
      </Paper>
    </div>
  );
};
export default ActivatingEmailInput;
